import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, NgZone } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, catchError, debounceTime, firstValueFrom, fromEvent, map, Observable, of } from "rxjs";

import { Buffer } from "buffer";
import { ETRAINING_DB } from "src/app/app.module";
import { CONSTANT } from "src/app/constant";
import * as uuid from "uuid";
(window as any).Buffer = Buffer;

import {
	CandidateSummary,
	DraftItem,
	OBAttempt,
	Obserevations,
	ObserevationsResp,
	RatingCandidate,
	StagingData,
	StagingResponse,
	TempDraftItem,
} from "src/app/models/models";

import moment from "moment";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { GoogleAuthService } from "src/app/services/google-api.service";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { DraftService } from "src/app/shared/services/draft.service";
import {
	backupUnsyncedDraftsFromIndexedDb,
	restoreUnsyncedDraftBackupToIndexedDb,
} from "src/app/shared/services/e-training-draft-backup.utils";
import { HttpService } from "src/app/shared/services/http.service";
import {
	resolveHttpErrorMessage,
	resolveMaintenanceModalContent,
	resolveSignOffHttpErrorPresentation,
	toHttpErrorPayload,
} from "src/app/shared/utils/http-error-message";
import { environment } from "src/environments/environment";
import { SESSION } from "src/session.config";
import {
	applySyncResultToLocalDraft,
	resolveMemoryDraftAdoptMode,
	resolveStagingSyncStatus,
	shouldApplyStagingSyncResult,
	StagingSyncResult,
} from "../../shared-v2/utils/draft-sync-result.utils";
import { withFormConfigVersion } from "../../shared-v2/utils/form-config-version.utils";
import { buildDraftUpsertRecord } from "../../shared-v2/utils/draft-upsert.utils";
import {
	isTs1V3OrV4DraftAssessment,
	resolveInitialRatingFormVersion,
} from "../../shared-v2/utils/rating-form-version.utils";
import { mapFormTypeCode } from "../../shared-v2/utils/rating-session-bootstrap.utils";
import { withTs1V3DisplaySnapshots } from "../../ts1-rating-form-v3/utils/ts1-rating-display-snapshot.utils";
import { stripV3DraftOnlyFields } from "../../ts1-rating-form-v3/utils/ts1-rating-ob-interaction";

@Injectable({
	providedIn: "root",
})
export class ContainerRatingsService {
	// ===================================
	// CONSTANTS & CONFIGURATION
	// ===================================

	errorMessages: any = CONSTANT.ERROR_MESSAGES;
	private readonly ONE_HOUR = 60 * 60 * 1000;
	private readonly BG_SYNC_TAG = "sync-drafts"; // <<<<<< unified tag
	private readonly _draftSyncUrl = "/api/etraining/drafts/sync"; // Reserved for future use
	private readonly ETRAINING_DB_NAME = "eTrainingDB";

	// ===================================
	// REACTIVE DATA STREAMS
	// ===================================

	private observationData$ = new BehaviorSubject<Obserevations[] | null>(null);
	private sessionData$ = new BehaviorSubject<CandidateSummary | null>(null);
	private formData$ = new BehaviorSubject<any | null>(null);
	private clearComments$ = new BehaviorSubject<any | null>(null);
	private manueversRawData$ = new BehaviorSubject<any | null>(null);
	private formType$ = new BehaviorSubject<any | null>(null);
	private minimumDate$ = new BehaviorSubject<any | null>(null);
	private initialDate$ = new BehaviorSubject<any | null>(null);

	// ===================================
	// DRAFT STATE MANAGEMENT
	// ===================================

	private _inDraftMode: boolean = false;
	private _draft?: DraftItem;
	private _tempDraft?: Partial<TempDraftItem>;
	/** Survives component hot reload so the container can restore routing before IndexedDB resolves. */
	private _activeRouteKey: string | null = null;
	public silenceAutosave = false; // gate autosave while we mutate
	/** Serialize IndexedDB writes per draft id to avoid lost updates. */
	private readonly draftOpQueues = new Map<string, Promise<void>>();
	/** Bumped on each optimistic in-memory edit so queued writes cannot clobber newer UI state. */
	private readonly draftMemoryEpochById = new Map<string, number>();
	/** Coalesce staging HTTP after rapid IDB writes (Step 3/4 edits). */
	private readonly pendingStagingSync = new Map<
		string,
		{ timer: ReturnType<typeof setTimeout>; memoryEpoch?: number }
	>();
	/** Latest staging POST per draft. An older 201 must not clear synced after a newer save. */
	private readonly stagingSyncGeneration = new Map<string, number>();
	private static readonly STAGING_SYNC_DEBOUNCE_MS = 1200;
	/** Bound sign-off retries when a local edit lands during the staging POST. */
	private static readonly STAGING_SYNC_MAX_ATTEMPTS = 4;

	constructor(
		private dialog: MatDialog,
		private httpService: HttpService,
		private _http: HttpClient, // reserved for future HTTP ops
		@Inject(ETRAINING_DB) private eTrainingDb: NgxIndexedDBService,
		private ngZone: NgZone,
		private draftService: DraftService,
		private googleAuthService: GoogleAuthService,
	) {
		this.initializeService();
	}

	private resettingETrainingDb = false;

	/**
	 * Serialize IndexedDB mutations per draft id so concurrent edits cannot clobber each other.
	 * Staging HTTP must NOT run inside this queue — only short IDB reads/writes — so optimistic
	 * persists are not blocked behind network latency.
	 */
	private enqueueDraftOp<T>(id: string, op: () => Promise<T>): Promise<T> {
		const prev = this.draftOpQueues.get(id) ?? Promise.resolve();
		const run = prev.catch(() => undefined).then(op);
		this.draftOpQueues.set(
			id,
			run.then(
				() => undefined,
				() => undefined,
			),
		);
		return run;
	}

	private bumpDraftMemoryEpoch(draftId: string): number {
		const next = (this.draftMemoryEpochById.get(draftId) ?? 0) + 1;
		this.draftMemoryEpochById.set(draftId, next);
		return next;
	}

	private getDraftMemoryEpoch(draftId: string): number {
		return this.draftMemoryEpochById.get(draftId) ?? 0;
	}

	private adoptSyncedDraftInMemory(draftId: string, next: DraftItem, epochAtStart?: number): void {
		if (this._draft?.id !== draftId) return;

		const mode = resolveMemoryDraftAdoptMode({
			memoryEpoch: this.getDraftMemoryEpoch(draftId),
			epochAtStart,
			memLastUpdated: this._draft.lastUpdatedDate,
			nextLastUpdated: next.lastUpdatedDate,
		});

		if (mode === "keep_unsynced") {
			// Memory is ahead of this op's snapshot; never adopt synced:true for content that was not posted.
			this._draft = { ...this._draft, synced: false };
			return;
		}
		this._draft = next;
	}

	private isVersionError(err: any): boolean {
		if (!err) return false;
		const name = (err as DOMException)?.name ?? (err as any)?.name ?? "";
		const message = (err as any)?.message ?? "";
		return name === "VersionError" || message.includes("VersionError");
	}

	private async resetETrainingIndexedDb(): Promise<void> {
		if (typeof indexedDB === "undefined") return;

		const backedUp = await backupUnsyncedDraftsFromIndexedDb(this.ETRAINING_DB_NAME);

		await new Promise<void>((resolve) => {
			const req = indexedDB.deleteDatabase(this.ETRAINING_DB_NAME);
			req.onerror = () => resolve();
			req.onblocked = () => resolve();
			req.onsuccess = () => resolve();
		});

		if (backedUp > 0) {
			console.warn(
				`Deleted eTrainingDB due to version mismatch. ${backedUp} unsynced draft(s) were backed up to localStorage (` +
					`${SESSION.SESSION_KEY_UNSYNCED_DRAFT_BACKUP}) and will be restored on next DB init.`,
			);
		} else {
			console.warn("Deleted eTrainingDB due to version mismatch. Local drafts may need to be re-created.");
		}
	}

	private async handleETrainingDbVersionError(err: any): Promise<boolean> {
		if (!this.isVersionError(err) || this.resettingETrainingDb) {
			return false;
		}

		this.resettingETrainingDb = true;
		try {
			await this.resetETrainingIndexedDb();
			return true;
		} catch (resetErr) {
			console.error("Failed to reset eTrainingDB after version mismatch:", resetErr);
			return false;
		} finally {
			this.resettingETrainingDb = false;
		}
	}

	// -----------------------------------
	// Init & Online listener
	// -----------------------------------
	private initializeService(): void {
		this.setupOnlineEventListener();
		console.log("Container Ratings Service initialized");
	}

	private setupOnlineEventListener(): void {
		fromEvent(window, "online")
			.pipe(debounceTime(300)) // avoid quick double fires
			.subscribe(() => {
				this.ngZone.run(async () => {
					console.log("Connection restored, syncing data...");
					// Wait for BFF session probe — isAuthenticated() is false until configure() finishes.
					if (await this.googleAuthService.ensureSessionResolved()) {
						await this.syncPendingDrafts();
						// If we have a current draft, reload it to update synced flag
						if (this._draft?.id) {
							await this.loadDraftItem(this._draft.id);
						}
					}
				});
			});
	}

	// Call once at app startup (AppComponent.ngOnInit)
	async kickoffGlobalSync(): Promise<void> {
		await restoreUnsyncedDraftBackupToIndexedDb(this.ETRAINING_DB_NAME);

		// Await session resolve so we don't skip sync while configure() is still in flight.
		if (navigator.onLine && (await this.googleAuthService.ensureSessionResolved())) {
			console.log("kickoffGlobalSync");
			await this.syncPendingDrafts();
			// await this.syncPendingData(); // dont need this
		}
		await this.scheduleBackgroundSync();
	}

	// IMPORTANT: talk to the *custom* SW registration at /et-sync/
	private async getCustomSwRegistration(): Promise<ServiceWorkerRegistration | undefined> {
		if (!("serviceWorker" in navigator)) return;
		try {
			const reg = await navigator.serviceWorker.getRegistration("/et-sync/");
			return reg ?? undefined;
		} catch {
			return undefined;
		}
	}

	private async scheduleBackgroundSync(): Promise<void> {
		try {
			const reg = await this.getCustomSwRegistration();
			if (!reg) return;

			// Tell the custom SW the API base (harmless to send multiple times)
			const base = environment.apiDomain || window.location.origin;
			(reg.active || (reg as any).waiting || (reg as any).installing)?.postMessage({
				type: "SET_API_BASE",
				base,
			});

			// Do NOT register periodic sync here.
			// CustomSwRegistrarService already owns periodic background sync.
		} catch {
			// ignore; foreground sync path still works
		}
	}

	private async scheduleOneShotSync(): Promise<void> {
		if (!("serviceWorker" in navigator)) return;
		try {
			const reg = await navigator.serviceWorker.getRegistration("/et-sync/");
			if (!reg) return;

			// Feature-detect SyncManager; Safari will just skip this path.
			// getTags is optional; registration is idempotent so it's fine
			// to call register multiple times.
			// @ts-ignore (Chromium)
			if ("sync" in reg) await reg.sync.register(this.BG_SYNC_TAG);
		} catch {
			// Ignore — not supported / permission denied
		}
	}

	clearDraft(): void {
		if (this._draft?.id) {
			this.draftMemoryEpochById.delete(this._draft.id);
		}
		this.draftService.clearDraft();
		this._draft = undefined;
	}

	setActiveRouteKey(routeKey: string): void {
		this._activeRouteKey = routeKey;
	}

	getActiveRouteKey(): string | null {
		return this._activeRouteKey;
	}

	private stampInitialRatingFormVersionOnCreate(record: DraftItem): void {
		if (record.ratingFormVersion != null && !Number.isNaN(Number(record.ratingFormVersion))) {
			return;
		}
		const formType = mapFormTypeCode(record?.formDetails?.formType?.code);
		const version = resolveInitialRatingFormVersion(formType, record.formDetails, record);
		if (version != null) {
			record.ratingFormVersion = version;
		}
	}

	// --------------------------
	// Observation data
	// --------------------------
	setObservationData(data: Obserevations[]): void {
		this.observationData$.next(data);
	}
	setManueversRawData(data: any): void {
		this.manueversRawData$.next(data);
	}
	setMinimumDate(data: any): void {
		this.minimumDate$.next(data);
	}
	setInitialDate(data: any): void {
		this.initialDate$.next(data);
	}
	setFormType(data: any): void {
		this.formType$.next(data);
	}
	getManueversRawData(): Observable<any | null> {
		return this.manueversRawData$.asObservable();
	}
	getObservationData(): Observable<Obserevations[] | null> {
		return this.observationData$.asObservable();
	}
	getObservationSnapshot(): Obserevations[] | null {
		return this.observationData$.getValue();
	}
	getMinimumDate(): Date {
		return this.minimumDate$.getValue();
	}
	getInitialDate(): Date {
		return this.initialDate$.getValue();
	}

	get observationsSnapshot(): Obserevations[] | null {
		return this.getObservationSnapshot();
	}
	clearObservationData(): void {
		this.observationData$.next(null);
	}

	setClearComments(): void {
		this.clearComments$.next(true);
	}
	getClearComments(): Observable<any | null> {
		return this.clearComments$.asObservable();
	}

	// --------------------------
	// Session data
	// --------------------------
	setSessionData(data: any): void {
		this.sessionData$.next(data);
	}
	getSessionData(): Observable<any> {
		return this.sessionData$.asObservable();
	}
	getSessionSnapshot(): any {
		return this.sessionData$.getValue();
	}
	clearSessionData(): void {
		this.sessionData$.next(null);
	}

	// --------------------------
	// Legacy FORM store helpers
	// --------------------------
	async setFormData(data: any): Promise<void> {
		this.formData$.next(data);

		if (navigator.onLine && !this.inDraftMode) {
			await this.saveToIndexedDB(data);
			this.syncDataToAPI(data);
		} else {
			await this.saveToIndexedDB(data);
		}
	}
	getFormData(): Observable<any | null> {
		return this.formData$.asObservable();
	}
	getFormSnapshot(): any | null {
		return this.formData$.getValue();
	}
	clearFormData(): void {
		this.formData$.next(null);
	}

	groupObservationsByCategory(observations: ObserevationsResp[]): Record<string, ObserevationsResp[]> {
		const result: Record<string, ObserevationsResp[]> = {};
		observations.forEach((item: ObserevationsResp) => {
			const { description } = item.category;
			if (!result[description]) result[description] = [];
			result[description].push(item);
		});
		return result;
	}

	generateCategoryObj(): any {
		const result: { [key: string]: { [key: string]: OBAttempt } } = {};

		const msd = this.getObservationSnapshot();

		msd?.forEach((item: any) => {
			const category = item.observation_category.description;
			const itemName = item.name;
			if (!result[category]) result[category] = {};
			result[category][itemName] = {
				currentCount: 0,
				preDefined: false,
				totalCount: 0,
				lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
				fixedManeuverCode: item.fixedManeuver.code,
			};
		});

		const categoryCounts: { [key: string]: number } = {};
		Object.keys(result).forEach((category) => {
			categoryCounts[category] = Object.keys(result[category]).length;
		});

		Object.keys(categoryCounts).forEach((category) => {
			if (!result[category]) result[category] = {};
			for (let i = 1; i <= categoryCounts[category]; i++) {
				const itemName = `${category}${i}`;
				result[category][itemName] = {
					currentCount: 0,
					preDefined: false,
					totalCount: 0,
					lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
					fixedManeuverCode: "",
				};
			}
		});

		return result;
	}

	generateCategoryObjFixedManeuver(fixedManeuverCodes: string = ""): any {
		const result: { [key: string]: { [key: string]: OBAttempt } } = {};

		let msd: any = [];
		if (fixedManeuverCodes) {
			msd = this.getObservationSnapshot()?.filter((observation: any) => {
				return observation?.fixedManeuver?.fixed_maneuver == fixedManeuverCodes;
			});
		} else {
			msd = this.getObservationSnapshot();
		}

		msd?.forEach((item: any) => {
			const category = item.observation_category.description;
			const itemName = item.name;
			if (!result[category]) result[category] = {};
			result[category][itemName] = {
				currentCount: 0,
				preDefined: false,
				totalCount: 0,
				lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
				fixedManeuverCode: fixedManeuverCodes,
			};
		});

		const categoryCounts: { [key: string]: number } = {};
		Object.keys(result).forEach((category) => {
			categoryCounts[category] = Object.keys(result[category]).length;
		});

		Object.keys(categoryCounts).forEach((category) => {
			if (!result[category]) result[category] = {};
			for (let i = 1; i <= categoryCounts[category]; i++) {
				const itemName = `${category}${i}`;
				result[category][itemName] = {
					currentCount: 0,
					preDefined: false,
					totalCount: 0,
					lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
					fixedManeuverCode: "",
				};
			}
		});

		return result;
	}

	private async saveToIndexedDB(data: any): Promise<void> {
		const record = {
			id: data.id || Date.now().toString(),
			data,
			stringify: JSON.stringify(data),
			toBeSynced: true,
			synced: false,
		};

		try {
			await this.eTrainingDb.update(CONSTANT.INDEXEDDB.FORM, record);
		} catch {
			await this.eTrainingDb.add(CONSTANT.INDEXEDDB.FORM, record);
		}
	}

	private syncDataToAPI(_data: any): void {
		if (this.inDraftMode) return;
		// placeholder no-op
	}

	async syncPendingData(): Promise<void> {
		if (this.inDraftMode) return;

		try {
			const pending: any[] = await firstValueFrom(
				this.eTrainingDb.getAllByIndex(CONSTANT.INDEXEDDB.FORM, "toBeSynced", IDBKeyRange.only(true)),
			);
			if (!pending || pending.length === 0) return;
			pending.forEach((_record) => {
				this.syncDataToAPI(_record.data);
			});
		} catch (err) {
			console.error("Error reading pending FORM data:", err);
		}
	}

	// -------- eTrainingDB Draft Helpers (TS6 sync path) --------

	async getDraftById(id: string): Promise<any | undefined> {
		try {
			return await firstValueFrom(this.eTrainingDb.getByID(CONSTANT.INDEXEDDB.DRAFT, id));
		} catch (err) {
			console.error("getDraftById failed:", err);
			return undefined;
		}
	}

	async addDraft(id: string, data: any): Promise<any> {
		const res = await this.enqueueDraftOp(id, async () => {
			console.log("add draft called");
			const record = { id, ...data } as DraftItem;
			this.stampInitialRatingFormVersionOnCreate(record);
			return firstValueFrom(this.eTrainingDb.add(CONSTANT.INDEXEDDB.DRAFT, withTs1V3DisplaySnapshots(record)));
		});
		await this.afterDraftPersisted(id);
		return res;
	}

	async updateDraft(id: string, data: Partial<DraftItem>): Promise<DraftItem | undefined> {
		const updated = await this.enqueueDraftOp(id, async () => {
			console.log("updateDraft called");

			const existing = await this.getDraftById(id);
			if (!existing) {
				const toInsert = { id, ...(data as any) } as DraftItem;
				this.stampInitialRatingFormVersionOnCreate(toInsert);
				const persisted = withTs1V3DisplaySnapshots(toInsert);
				await firstValueFrom(this.eTrainingDb.add(CONSTANT.INDEXEDDB.DRAFT, persisted));
				this._draft = persisted;
				return persisted;
			}

			const next: DraftItem = { ...existing, id: existing.id };

			const replaceKeys: (keyof DraftItem)[] = [
				"trainingInformation",
				"candidateRating",
				"signOffDetails",
				"toBeSynced",
			];

			for (const k of replaceKeys) {
				if (k in data) {
					// @ts-expect-error dynamic assign
					next[k] = (data as any)[k];
				}
			}

			for (const [k, v] of Object.entries(data)) {
				if (!replaceKeys.includes(k as keyof DraftItem)) {
					// @ts-expect-error dynamic assign
					next[k] = v as any;
				}
			}

			const persisted = withTs1V3DisplaySnapshots(next);
			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, persisted));
			this._draft = persisted;
			return persisted;
		});
		await this.afterDraftPersisted(id);
		return updated;
	}

	async upsertDraft(
		draftId: string | undefined,
		data: Partial<DraftItem>,
		continuePage: number,
		formDetails?: any,
		opts: { forcePage?: boolean } = {}, // NEW
	): Promise<string> {
		const id = draftId ?? uuid.v4();
		const memory = this._draft?.id === id ? this._draft : undefined;
		const protectTs1Memory = isTs1V3OrV4DraftAssessment(
			data.ratingFormVersion ?? memory?.ratingFormVersion,
			data.formDetails?.formType?.code ?? memory?.formDetails?.formType?.code,
		);
		// Snapshot before enqueue. A later rating bumps the epoch and must keep its in-memory log.
		const memoryEpoch = protectTs1Memory ? this.getDraftMemoryEpoch(id) : undefined;

		await this.enqueueDraftOp(id, async () => {
			console.log("upsert draft called");

			const existing = await this.getDraftById(id);

			const nextPage = opts.forcePage
				? (continuePage ?? existing?.continuePage ?? 1) // allow going DOWN
				: Math.max(existing?.continuePage ?? 1, continuePage ?? 1);

			const payload = buildDraftUpsertRecord({
				id,
				existing,
				delta: data,
				continuePage: nextPage,
				formDetails,
				lastUpdatedDate: new Date().toISOString(),
			});

			if (!existing) {
				this.stampInitialRatingFormVersionOnCreate(payload);
			}

			const persisted = withTs1V3DisplaySnapshots(payload);
			const ts1Memory = isTs1V3OrV4DraftAssessment(
				persisted.ratingFormVersion ?? existing?.ratingFormVersion,
				persisted.formDetails?.formType?.code ?? existing?.formDetails?.formType?.code,
			);

			if (existing) {
				await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, persisted));
			} else {
				await firstValueFrom(this.eTrainingDb.add(CONSTANT.INDEXEDDB.DRAFT, persisted));
			}

			if (this._draft?.id === id && ts1Memory) {
				this.adoptSyncedDraftInMemory(id, persisted, memoryEpoch);
			} else {
				this._draft = persisted;
				if (!ts1Memory) {
					void this.loadDraftItem(id);
				}
			}
			console.log("upsert draft done");
		});
		// Do not await HTTP staging sync — callers (e.g. Back) must not block on network.
		void this.afterDraftPersisted(id, memoryEpoch);
		return id;
	}

	async deleteDraft(id: string): Promise<void> {
		await this.enqueueDraftOp(id, async () => {
			try {
				await firstValueFrom(this.eTrainingDb.delete(CONSTANT.INDEXEDDB.DRAFT, id));
				this.draftMemoryEpochById.delete(id);
				if (this._draft?.id === id) {
					this.draftService.clearDraft();
					this._draft = undefined;
				}
			} catch (err) {
				console.error("deleteDraft failed:", err);
			}
		});
	}

	set inDraftMode(inDraftMode: boolean) {
		this._inDraftMode = inDraftMode;
	}
	get inDraftMode(): boolean {
		return this._inDraftMode;
	}
	get isSynced(): boolean {
		return this._draft?.synced ?? false;
	}
	get tempDraft(): Partial<TempDraftItem> {
		return this._tempDraft ?? {};
	}
	set tempDraft(draftItem: Partial<TempDraftItem>) {
		this._tempDraft = { ...this._tempDraft, ...draftItem };
	}
	clearTempDraft() {
		this._tempDraft = {};
	}

	async loadDraftItem(draftId: string) {
		return new Promise<void>((resolve) => {
			this.eTrainingDb.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe({
				next: (draft) => {
					if (!draft) {
						resolve();
						return;
					}

					let normalized = draft;
					const formSnap = this.formData$.getValue();
					if (formSnap?.configVersion != null && normalized.formDetails?.configVersion == null) {
						normalized = {
							...normalized,
							formDetails: withFormConfigVersion(normalized.formDetails, Number(formSnap.configVersion)),
						};
					}

					this._draft = normalized;
					this._inDraftMode = true;

					resolve();
				},
				error: (err) => {
					console.error(err);
					resolve();
				},
				complete: () => {},
			});
		});
	}

	updateSelectedDraft(data: Partial<DraftItem>): Promise<void> {
		if (!this._draft?.id) return Promise.resolve();
		const draftId = this._draft.id;
		// Capture stamp + epoch at enqueue time so a later write-time Date.now() cannot
		// look "newer" than subsequent optimistic edits and clobber them in memory.
		const lastUpdatedDate = new Date().toISOString();
		const memoryEpoch = this.bumpDraftMemoryEpoch(draftId);
		this._draft = withTs1V3DisplaySnapshots({
			...this._draft,
			...data,
			lastUpdatedDate,
		} as DraftItem);

		return this.enqueueDraftOp(draftId, async () => {
			const record = await this.getDraftById(draftId);
			if (!record) return;

			const merged = withTs1V3DisplaySnapshots({
				...record,
				...data,
				lastUpdatedDate,
				synced: false,
			} as DraftItem);
			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, merged));
			// Keep optimistic `_draft` as-is: assigning `merged` here can wipe newer in-memory patches
			// that landed while this queued op waited (see resolveMemoryDraftAdoptMode / memoryEpoch).
		})
			.then(() => this.afterDraftPersisted(draftId, memoryEpoch))
			.catch((err) => {
				console.error(err);
			});
	}

	persistContinuePage(continuePage: number) {
		console.log("persist continue page called");
		this.updateSelectedDraft({ continuePage });
	}

	deleteSelectedDraft() {
		const draftId = this._draft?.id;
		if (!draftId) return;
		void this.deleteDraft(draftId).catch(console.error);
	}

	async deleteCandidateForSelectedDraft(idx: number): Promise<void> {
		if (!this._draft?.id) return;
		const draftId = this._draft.id;

		const record = await firstValueFrom(this.eTrainingDb.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId));
		if (!record?.trainingInformation?.candidates?.length) return;

		const original = record.trainingInformation.candidates ?? [];
		const sorted = [...original].sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
		if (idx < 0 || idx >= sorted.length) return;

		const candidateId: string | undefined = sorted[idx]?.candidateId;
		if (!candidateId) return;

		const kept = original.filter((c) => c.candidateId !== candidateId);

		let candidates = kept;
		if (kept.length === 1) {
			candidates = [{ ...kept[0], isPrimary: true }];
		} else if (kept.length > 1 && !kept.some((c) => c.isPrimary)) {
			candidates = kept.map((c, i) => (i === 0 ? { ...c, isPrimary: true } : { ...c }));
		}

		const cr = record.candidateRating ?? {};
		const nextCandidateRating = {
			...cr,
			addRemoveOb: (cr.addRemoveOb ?? []).filter((x: any) => x.candidateId !== candidateId),
			selectedOBs: (cr.selectedOBs ?? []).filter((x: any) => x.candidateId !== candidateId),
			temEvaluations: (cr.temEvaluations ?? []).filter((x: any) => x.candidateId !== candidateId),
			otherComments: (cr.otherComments ?? []).filter((x: any) => x.candidateId !== candidateId),
			resetManeuvers: (cr.resetManeuvers ?? []).filter((x: any) => x.candidateId !== candidateId),
			temRate: Object.fromEntries(Object.entries(cr.temRate ?? {}).filter(([k]) => !k.startsWith(`${candidateId}.`))),
		};

		const sod = record.signOffDetails ?? { candidateSignOffDetails: {} as Record<string, any> };
		const { [candidateId]: _drop, ...rest } = (sod.candidateSignOffDetails ?? {}) as Record<string, any>;
		const nextSignOffDetails = { candidateSignOffDetails: rest };

		const nextToBeSynced = (record.toBeSynced ?? []).filter((e: any) => e?.body?.candidateId !== candidateId);

		await this.updateDraft(draftId, {
			trainingInformation: { ...(record.trainingInformation ?? {}), candidates },
			candidateRating: nextCandidateRating,
			signOffDetails: nextSignOffDetails,
			toBeSynced: nextToBeSynced,
			continuePage: 1,
			lastUpdatedDate: new Date().toISOString(),
		});
	}

	onSaveToDraftsSuccess(toList: boolean = false): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "save-to-drafts-success-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Successful",
				modalContent: "Assessment saved to draft successfully!",
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((_result: any) => {
			sub.unsubscribe();
		});
	}

	blockSubmitIfOffline(_toList: boolean = false): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "submit-offline-modal",
			height: "240px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Failed",
				modalContent:
					'You can only proceed to sign off when you are connected to the internet. Please proceed to "Save as draft" and sign off when you are connected to the internet.',
				modalBtnConfirmText: "Save As Draft",
				noCloseBtn: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe(() => {
			this.updateSelectedDraft({ continuePage: 3 });
			this.onSaveToDraftsSuccess(_toList);
			sub.unsubscribe();
		});
	}

	/** Staging HTTP failure (not a dropped connection). Does not ask the user to reconnect. */
	presentStagingSyncError(error: unknown): void {
		const message = resolveHttpErrorMessage(error);
		this.dialog.open(CbtaModalComponent, {
			panelClass: "submit-offline-modal",
			height: "240px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Failed",
				modalContent: message,
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});
	}

	/** Same dialog as ApiHandlerService maintenance handling (HTTP 503). */
	presentMaintenanceModal(error: unknown): void {
		const message = resolveMaintenanceModalContent(error) ?? [];
		this.dialog.open(CbtaModalComponent, {
			panelClass: "error-modal",
			height: "220px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalName: "error-modal",
				modalTitle: "Under Maintenance",
				modalContent: message,
				noAction: true,
				disableClose: true,
			},
		});
	}

	/** Sign-off credential / HTTP failure. Status 0 is left to the caller so it can show the offline dialog. */
	presentSignOffHttpError(error: unknown): void {
		const { title, message } = resolveSignOffHttpErrorPresentation(error);
		this.dialog.open(CbtaModalComponent, {
			panelClass: "error-modal",
			height: "220px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalName: "error-modal",
				modalTitle: title,
				modalContent: message,
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});
	}

	addToBeSynched(eventName: string, body: any) {
		const data = { eventName, body };
		const toBeSynced = this._draft?.toBeSynced ?? [];
		toBeSynced.push(data);
		this.updateSelectedDraft({ toBeSynced });
	}

	// intentionally empty (kept original behavior)
	syncSelectedDraft() {}

	calculateRating(id: string, candidates: RatingCandidate[]) {
		for (const candidate of candidates) {
			const candidateId = candidate.candidateId;
			if (candidateId) {
				this.httpService.calculateRating(id, candidateId).subscribe(
					(resp: any) => {
						const signOffDetails = this.draft?.signOffDetails;
						this.updateSelectedDraft({
							signOffDetails: {
								candidateSignOffDetails: {
									...signOffDetails?.candidateSignOffDetails,
									[candidateId]: {
										...signOffDetails?.candidateSignOffDetails?.[candidateId],
										...resp.data,
									},
								},
							},
							continuePage: 3,
						});
					},
					(error) => {
						console.error(`calculation error ${candidateId}`, error);
					},
				);
			} else {
				console.error(`undefined id:`, candidateId);
			}
		}
	}

	get draft(): DraftItem | undefined {
		return this._draft;
	}

	get formType(): string | undefined {
		return this.formType$.getValue();
	}

	private isTs6Draft(draft: any | undefined): boolean {
		const code = draft?.formDetails?.formType?.code ?? draft?.formDetails?.formType ?? "";
		return String(code).toLowerCase() === CONSTANT.FORM_TYPE.ts_6;
	}

	/**
	 * Kick staging sync (or offline unsynced mark) after an IDB persist.
	 * Debounced so rapid Step 3/4 edits do not serialize full draft HTTP uploads.
	 */
	private async afterDraftPersisted(id: string, memoryEpoch?: number): Promise<void> {
		this.scheduleStagingSync(id, memoryEpoch);
	}

	private scheduleStagingSync(id: string, memoryEpoch?: number): void {
		const existing = this.pendingStagingSync.get(id);
		if (existing) {
			clearTimeout(existing.timer);
		}
		const epoch = memoryEpoch ?? existing?.memoryEpoch;
		const timer = setTimeout(() => {
			this.pendingStagingSync.delete(id);
			void this.runStagingSync(id, epoch);
		}, ContainerRatingsService.STAGING_SYNC_DEBOUNCE_MS);
		this.pendingStagingSync.set(id, {
			timer,
			memoryEpoch: epoch,
		});
	}

	private async runStagingSync(id: string, memoryEpoch?: number): Promise<void> {
		console.log("onDraftChanged", id);
		const record = await this.getDraftById(id);
		if (!record) return;

		try {
			if (navigator.onLine) {
				await this.syncOneDraftBody(record, memoryEpoch);
			} else {
				await this.markDraftUnsynced(id, memoryEpoch);
			}
		} catch (err) {
			console.error("sync/mark failed:", err);
		}

		await this.scheduleOneShotSync();
	}

	/**
	 * Flush debounced staging sync before submit / send-to-candidate.
	 * Retries while a newer local edit invalidates the posted snapshot.
	 * Attempts the POST even when `navigator.onLine` is false so a stale offline flag cannot block sign-off.
	 */
	async flushStagingSync(draftId?: string): Promise<StagingSyncResult> {
		const ids = draftId ? [draftId] : [...this.pendingStagingSync.keys()];
		if (ids.length === 0) return { status: "synced" };

		let last: StagingSyncResult = { status: "synced" };
		for (const id of ids) {
			last = await this.flushDraftUntilSettled(id);
			if (last.status === "offline" || last.status === "http_error") return last;
		}
		return last;
	}

	private async flushDraftUntilSettled(id: string): Promise<StagingSyncResult> {
		let last: StagingSyncResult = { status: "retry" };
		for (let attempt = 0; attempt < ContainerRatingsService.STAGING_SYNC_MAX_ATTEMPTS; attempt++) {
			const pending = this.pendingStagingSync.get(id);
			if (pending) {
				clearTimeout(pending.timer);
				this.pendingStagingSync.delete(id);
			}

			const record = await this.getDraftById(id);
			if (!record) return { status: "synced" };
			if (!pending && record.synced === true) return { status: "synced" };

			last = await this.syncOneDraftBody(record, pending?.memoryEpoch);
			if (last.status !== "retry") return last;
		}

		return {
			status: "http_error",
			error: last.error ?? new Error("The assessment could not be synced. Please try again."),
		};
	}

	private async markDraftUnsynced(id: string, memoryEpoch?: number) {
		await this.enqueueDraftOp(id, async () => {
			const existing = await this.getDraftById(id);
			if (!existing) return;
			const updated = { ...existing, synced: false, lastUpdatedDate: existing.lastUpdatedDate };
			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, updated));
			this.adoptSyncedDraftInMemory(id, updated, memoryEpoch);
		});
	}

	private sendDraftToApi(draft: DraftItem): Observable<{ ok: true } | { ok: false; status: number; error: unknown }> {
		const dataToSend: StagingData = {
			trainingAssessmentId: draft.id,
			formId: draft.trainingInformation.formId,
			formType: draft.formDetails.formType.code,
			assessment: stripV3DraftOnlyFields(draft),
			// Deprecated: duplicate base64 of assessment used to inflate payloads past the API body limit.
			assessmentBase64: "",
			assessmentDate: moment(draft.trainingInformation.assessmentDate).format("YYYY-MM-DD"),
			assessmentName: draft.formDetails.name,
			version: 0,
		};

		return this.httpService.stagingDataAPI(dataToSend).pipe(
			map((resp: StagingResponse) => {
				const ok = resp?.success === undefined ? true : !!resp.success;
				return ok ? { ok: true as const } : { ok: false as const, status: 400, error: resp };
			}),
			catchError((err) => {
				const payload = toHttpErrorPayload(err);
				return of({ ok: false as const, status: payload.status, error: err });
			}),
		);
	}

	/**
	 * Staging sync core. HTTP runs outside the write queue; the post-response IDB update
	 * is re-queued so concurrent local edits are not overwritten.
	 */
	private async syncOneDraftBody(draft: DraftItem, memoryEpoch?: number): Promise<StagingSyncResult> {
		const draftId = draft?.id;
		if (!draftId) return { status: "http_error", error: new Error("Missing draft id") };

		const latest = (await this.getDraftById(draftId)) ?? draft;
		const snapshot: DraftItem = { ...latest };
		const generation = (this.stagingSyncGeneration.get(draftId) ?? 0) + 1;
		this.stagingSyncGeneration.set(draftId, generation);

		let post: { ok: true } | { ok: false; status: number; error: unknown };
		try {
			post = await firstValueFrom(this.sendDraftToApi({ ...snapshot, synced: true }));
		} catch (err) {
			const hasStatus = err != null && typeof err === "object" && "status" in err;
			post = { ok: false, status: hasStatus ? toHttpErrorPayload(err).status : 500, error: err };
		}

		let localUnchanged = true;
		await this.enqueueDraftOp(draftId, async () => {
			const latestGeneration = this.stagingSyncGeneration.get(draftId) ?? generation;
			if (!shouldApplyStagingSyncResult(generation, latestGeneration)) {
				return;
			}
			const current = await this.getDraftById(draftId);
			localUnchanged = (current?.lastUpdatedDate || "") === (snapshot.lastUpdatedDate || "");
			const next = applySyncResultToLocalDraft(snapshot, current, post.ok);
			if (!next) {
				return;
			}

			await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, next));
			this.adoptSyncedDraftInMemory(draftId, next, memoryEpoch);
		});

		return {
			status: resolveStagingSyncStatus({
				httpStatus: post.ok ? null : post.status,
				httpOk: post.ok,
				attemptGeneration: generation,
				latestGeneration: this.stagingSyncGeneration.get(draftId) ?? generation,
				localUnchanged,
			}),
			error: post.ok ? undefined : post.error,
		};
	}

	public async syncOneDraft(draft: DraftItem): Promise<void> {
		if (!draft?.id) return;
		const pending = this.pendingStagingSync.get(draft.id);
		if (pending) {
			clearTimeout(pending.timer);
			this.pendingStagingSync.delete(draft.id);
		}
		await this.syncOneDraftBody(draft, pending?.memoryEpoch);
	}

	public async syncPendingDrafts(): Promise<void> {
		try {
			console.log("syncPendingDrafts called");
			const all: DraftItem[] = await firstValueFrom(this.eTrainingDb.getAll(CONSTANT.INDEXEDDB.DRAFT));

			const pending = (all ?? []).filter((d) => d && d.synced !== true);

			if (pending.length === 0) {
				console.log("no data to sync..");
				return;
			}

			for (const draft of pending) {
				await this.syncOneDraft(draft);
			}
		} catch (err) {
			if (await this.handleETrainingDbVersionError(err)) {
				return;
			}
			console.error("Error syncing pending TS6 drafts:", err);
		}
	}
}
